import React, { useEffect, useMemo, useRef, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { getLeadById } from '../services/leadService';
import { deleteListingView, getListingConfiguration, saveListingPreferences, saveListingView, updateListingView } from '../services/listingPreferencesService';
import { formatDate as formatLocalDate } from '../utils/dateFormatter';
import { formatCurrency } from '../utils/currencyUtils';
import { useSettings } from '../context/SettingsContext';
import { formatStatusLabel } from '../utils/statusFormatter';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { CrmBadge, CrmButton, CrmTooltip } from './ui';
import ConfirmDialog from './ui/ConfirmDialog';
import {
  CrmAdvancedFilters,
  CrmColumnManager,
  CrmDataTable,
  CrmListPage,
  CrmRecordPreview,
  CrmSaveViewModal,
  useCrmListState,
} from './listing';

const CONTACTS_PER_PAGE = 20;
const VIEW_STORAGE_KEY = 'jdh:crm:list:contacts:views';

const FALLBACK_VISIBLE_FIELDS = [
  'email',
  'phone_number',
  'assigned_salesperson',
  'lead_status',
  'company_name',
  'created_at',
];

const KNOWN_CONTACT_FIELDS = [
  ...FALLBACK_VISIBLE_FIELDS,
  'updated_at',
  'priority',
  'follow_up_date',
  'amount',
  'hotness',
  'notes',
];

const IDENTITY_FIELDS = new Set(['first_name', 'last_name', 'contact_name', 'name']);

const FIELD_LABELS = {
  assigned_salesperson: 'Assigned salesperson',
  company_name: 'Company',
  created_at: 'Create date',
  email: 'Email',
  follow_up_date: 'Follow-up date',
  hotness: 'Hotness',
  lead_status: 'Status',
  phone_number: 'Phone number',
  priority: 'Priority',
  updated_at: 'Last activity date',
  legal_entity_id: 'Legal Entity',
  brand_id: 'Brand',
  business_unit_id: 'Business Unit',
  sales_channel_id: 'Sales Channel',
  storefront_id: 'Storefront',
};

const formatFieldLabel = (field) => FIELD_LABELS[field] || String(field || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase());

const getContactName = (contact = {}) => (
  [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim() ||
  contact.full_name ||
  contact.contact_name ||
  contact.name ||
  contact.company_name ||
  contact.email ||
  `Contact ${contact.id || ''}`.trim()
);

const getInitials = (value) => String(value || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();

const getOwnerId = (contact = {}) => {
  const value = contact.assigned_salesperson ?? contact.owner_id ?? contact.assigned_to ?? '';
  if (value && typeof value === 'object') {
    return value.id ?? value.user_id ?? '';
  }
  return value;
};

const getEmbeddedOwnerName = (contact = {}) => {
  const directName = contact.assigned_salesperson_name ?? contact.owner_name;
  if (directName) return directName;
  const value = contact.assigned_salesperson ?? contact.owner ?? contact.assigned_to;
  if (value && typeof value === 'object') {
    return value.name || value.full_name || value.username || value.email || '';
  }
  return '';
};

const readSavedViews = () => {
  try {
    const value = JSON.parse(localStorage.getItem(VIEW_STORAGE_KEY) || '[]');
    return Array.isArray(value)
      ? value.filter((view) => view?.id && view?.label && view?.config)
      : [];
  } catch {
    return [];
  }
};

const persistSavedViews = (views) => {
  try {
    localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(views));
  } catch {
    // Saved views still work for the current session when storage is unavailable.
  }
};

const getUniqueOptions = (items, accessor, formatter = (value) => value) => {
  const values = [...new Set(items.map(accessor).filter((value) => value !== '' && value !== null && value !== undefined))];
  return values
    .map((value) => ({ value, label: formatter(value) }))
    .sort((left, right) => String(left.label).localeCompare(String(right.label)));
};

const getBadgeTone = (value) => {
  const status = String(value || '').trim().toLowerCase().replace(/[_\s]+/g, '-');
  if (['won', 'closed', 'converted', 'active', 'customer'].includes(status)) return 'success';
  if (['lost', 'cancelled', 'rejected', 'inactive', 'disqualified'].includes(status)) return 'danger';
  if (['new', 'open', 'in-progress', 'qualified'].includes(status)) return 'info';
  if (['contacted', 'follow-up', 'proposal', 'negotiation', 'pending'].includes(status)) return 'warning';
  return 'info';
};

const isDateField = (field) => (
  field === 'created_at' ||
  field === 'updated_at' ||
  field === 'follow_up_date' ||
  field.endsWith('_date')
);

const exportContacts = (rows, fields, filename) => {
  const data = rows.map((contact) => {
    const item = { Name: getContactName(contact) };
    fields.forEach((field) => {
      item[formatFieldLabel(field)] = contact[field];
    });
    return item;
  });
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Contacts');
  XLSX.writeFile(workbook, filename);
};

const LeadsTable = ({
  leads = [],
  visibleFields = [],
  onBulkDelete,
  onDeleteContact,
  leadStatusOptions = [],
  priorityOptions = [],
  onUpdateLead,
  onImportBulk,
  loading = false,
  error = '',
  onRetry,
  onRefresh,
  columnConfigReady = true,
  users = [],
}) => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { currentUser } = useAuth();
  const { getUnreadNotificationFor, markRecordNotificationsSeen } = useNotification();
  const previewRequestRef = useRef(0);
  const defaultViewStateRef = useRef(null);
  const preferencesHydratedRef = useRef(false);
  const preferencesTimerRef = useRef(null);

  const [columnManagerOpen, setColumnManagerOpen] = useState(false);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [advancedFilterId, setAdvancedFilterId] = useState(null);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [chromeCollapsed, setChromeCollapsed] = useState(false);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [savedViews, setSavedViews] = useState(readSavedViews);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    mode: 'single',
    contactId: null,
    label: '',
    loading: false,
  });
  const [preview, setPreview] = useState({ open: false, id: null, record: null, loading: false, error: '' });

  useEffect(() => () => {
    previewRequestRef.current += 1;
  }, []);

  const configuredFields = useMemo(() => (
    visibleFields.length ? visibleFields : FALLBACK_VISIBLE_FIELDS
  ), [visibleFields]);
  const availableFields = useMemo(() => {
    const databaseFields = [...new Set(leads.flatMap((contact) => Object.keys(contact || {})))];
    const excluded = new Set(['id','first_name','last_name','contact_name','name','full_name','commercial_context_snapshot','legal_entity_name','legal_name','brand_name','business_unit_name','sales_channel_name','storefront_name']);
    return [...new Set([...configuredFields, ...KNOWN_CONTACT_FIELDS, ...databaseFields])].filter((field) => field && !excluded.has(field));
  }, [configuredFields, leads]);

  const ownerNameById = useMemo(() => {
    const directory = new Map();
    [...users, currentUser].filter(Boolean).forEach((user) => {
      const id = user?.id ?? user?.user_id;
      const name = user?.name || user?.full_name || user?.username || user?.email;
      if (id !== null && id !== undefined && name) directory.set(String(id), name);
    });
    return directory;
  }, [currentUser, users]);

  const getOwnerDisplay = (contact = {}) => {
    const embeddedName = getEmbeddedOwnerName(contact);
    if (embeddedName) return embeddedName;
    const ownerId = getOwnerId(contact);
    if (ownerId === '' || ownerId === null || ownerId === undefined) return '';
    const mappedName = ownerNameById.get(String(ownerId));
    if (mappedName) return mappedName;
    return /^\d+$/.test(String(ownerId).trim()) ? 'Unknown user' : String(ownerId);
  };

  const resolveContactBadge = (contact) => {
    const notification = getUnreadNotificationFor('leads', contact?.id);
    if (!notification) return '';
    const action = String(notification.action || '').toLowerCase();
    return /(create|new|added)/.test(action) ? 'NEW' : 'UPDATED';
  };

  const openFullRecord = async (contact) => {
    try {
      await markRecordNotificationsSeen('leads', contact.id);
    } catch (markError) {
      console.warn('Could not mark contact notifications as seen', markError);
    }
    navigate(`/leads/${contact.id}/edit`);
  };

  const handleRowClick = (contact) => openFullRecord(contact);

  const loadPreview = async (contact) => {
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    setPreview({ open: true, id: contact.id, record: contact, loading: true, error: '' });
    try {
      const response = await getLeadById(contact.id);
      if (previewRequestRef.current !== requestId) return;
      const record = response?.lead || response?.data || response || contact;
      setPreview({ open: true, id: contact.id, record, loading: false, error: '' });
    } catch (previewError) {
      if (previewRequestRef.current !== requestId) return;
      setPreview((previous) => ({
        ...previous,
        loading: false,
        error: previewError?.response?.data?.error || 'The contact preview could not be loaded.',
      }));
    }
  };

  const commercialContextValue = (contact, field) => {
    const map = {
      legal_entity_id: contact.legal_entity_name || contact.legal_name,
      brand_id: contact.brand_name,
      business_unit_id: contact.business_unit_name,
      sales_channel_id: contact.sales_channel_name,
      storefront_id: contact.storefront_name,
    };
    return map[field] || contact[field];
  };

  const renderFieldValue = (contact, field) => {
    const value = commercialContextValue(contact, field);
    if (value === null || value === undefined || value === '') return '—';
    if (isDateField(field)) return formatLocalDate(value) || '—';
    if (field === 'lead_status') return formatStatusLabel(value);
    if (field === 'amount' && Number.isFinite(Number(value))) return formatCurrency(Math.round(Number(value)), settings?.currency_code);
    if (field === 'hotness' && Number.isFinite(Number(value))) return Math.round(Number(value)).toLocaleString('en-IN', { maximumFractionDigits: 0 });
    return value;
  };

  const renderEditableField = (contact, field) => {
    const value = renderFieldValue(contact, field);
    if (field === 'email' && contact[field]) {
      return <a className="crm-data-link" href={`mailto:${contact[field]}`} onClick={(event) => event.stopPropagation()}>{value}</a>;
    }
    if (field === 'phone_number' && contact[field]) {
      return <a className="crm-data-link" href={`tel:${contact[field]}`} onClick={(event) => event.stopPropagation()}>{value}</a>;
    }
    if (field === 'lead_status') return <CrmBadge tone={getBadgeTone(value)}>{value}</CrmBadge>;
    if (field === 'assigned_salesperson') {
      const owner = getOwnerDisplay(contact);
      return owner ? (
        <span className="crm-owner-cell">
          <span className="crm-owner-cell__avatar" aria-hidden="true">{getInitials(owner)}</span>
          <span>{owner}</span>
        </span>
      ) : '—';
    }
    return String(value);
  };

  const columns = useMemo(() => {
    const primary = {
      id: 'contact_identity',
      label: 'Name',
      width: 260,
      minWidth: 240,
      maxWidth: 420,
      hideable: false,
      pinnable: true,
      searchable: true,
      accessor: getContactName,
      getTitle: getContactName,
      renderCell: (contact) => {
        const name = getContactName(contact);
        const badge = resolveContactBadge(contact);
        return (
          <span className="crm-contact-cell">
            <button
              type="button"
              className="crm-contact-cell__preview"
              onClick={(event) => {
                event.stopPropagation();
                loadPreview(contact);
              }}
              aria-label={`Preview ${name}`}
            >
              <ChevronRightIcon />
            </button>
            <span className="crm-contact-cell__avatar" aria-hidden="true">{getInitials(name)}</span>
            <a
              href={`/leads/${contact.id}/edit`}
              className="crm-contact-cell__link"
              onClick={(event) => {
                event.stopPropagation();
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                openFullRecord(contact);
              }}
            >
              {name}
            </a>
            {badge ? <CrmBadge tone={badge === 'NEW' ? 'danger' : 'warning'}>{badge}</CrmBadge> : null}
          </span>
        );
      },
    };

    const propertyColumns = availableFields.map((field) => ({
      id: field,
      field,
      label: field === 'amount' ? `Amount (${String(settings?.currency_code || 'INR').toUpperCase()})` : formatFieldLabel(field),
      width: field === 'email' || field === 'company_name' ? 240 : 180,
      minWidth: field === 'email' || field === 'company_name' ? 210 : 150,
      maxWidth: field === 'notes' ? 520 : 420,
      type: isDateField(field) ? 'date' : field === 'amount' ? 'currency' : field === 'hotness' ? 'number' : 'text',
      defaultHidden: !configuredFields.includes(field),
      filterable: ['assigned_salesperson', 'created_at', 'updated_at', 'lead_status', 'company_name', 'priority'].includes(field),
      accessor: (contact) => field === 'assigned_salesperson' ? getOwnerDisplay(contact) : commercialContextValue(contact, field),
      sortValue: (contact) => field === 'assigned_salesperson' ? getOwnerDisplay(contact) : commercialContextValue(contact, field),
      getTitle: (contact) => field === 'assigned_salesperson'
        ? getOwnerDisplay(contact)
        : String(renderFieldValue(contact, field)),
      renderCell: (contact) => renderEditableField(contact, field),
    }));

    const actions = {
      id: 'row_actions',
      label: 'Actions',
      width: 104,
      minWidth: 104,
      maxWidth: 132,
      hideable: false,
      sortable: false,
      searchable: false,
      editable: false,
      className: 'crm-data-table__actions-cell',
      renderCell: (contact) => (
        <span className="crm-row-actions">
          <CrmTooltip content={`Edit ${getContactName(contact)}`} placement="top">
            <CrmButton
              iconOnly
              size="small"
              variant="tertiary"
              className="crm-row-action"
              aria-label={`Edit ${getContactName(contact)}`}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/leads/${contact.id}/edit`);
              }}
            >
              <EditOutlinedIcon />
            </CrmButton>
          </CrmTooltip>
          <CrmTooltip content={`Delete ${getContactName(contact)}`} placement="top">
            <CrmButton
              iconOnly
              size="small"
              variant="tertiary"
              className="crm-row-action crm-row-action--danger"
              aria-label={`Delete ${getContactName(contact)}`}
              onClick={(event) => {
                event.stopPropagation();
                setDeleteDialog({
                  open: true,
                  mode: 'single',
                  contactId: contact.id,
                  label: getContactName(contact),
                  loading: false,
                });
              }}
            >
              <DeleteOutlineIcon />
            </CrmButton>
          </CrmTooltip>
        </span>
      ),
    };

    return [primary, ...propertyColumns, actions];
    // These renderers intentionally refresh with the inline-edit and notification context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableFields, configuredFields, leadStatusOptions, priorityOptions, ownerNameById, users, settings?.currency_code]);

  const views = useMemo(() => [
    { id: 'all', label: 'All contacts' },
    ...savedViews.map((view) => ({ id: view.id, label: view.label })),
  ], [savedViews]);

  const ownerOptions = useMemo(() => {
    const optionsById = new Map();
    leads.forEach((contact) => {
      const id = getOwnerId(contact);
      if (id === '' || id === null || id === undefined) return;
      optionsById.set(String(id), { value: id, label: getOwnerDisplay(contact) });
    });
    return [...optionsById.values()].sort((left, right) => left.label.localeCompare(right.label));
  }, [leads, ownerNameById]);
  const statusOptions = useMemo(() => {
    const options = [...new Set([
      ...leadStatusOptions,
      ...leads.map((contact) => contact.lead_status).filter(Boolean),
    ])];
    return options.map((value) => ({ value, label: formatStatusLabel(value) }));
  }, [leadStatusOptions, leads]);
  const priorityFilterOptions = useMemo(() => getUniqueOptions(
    leads,
    (contact) => contact.priority,
    formatFieldLabel
  ), [leads]);
  const companyOptions = useMemo(() => getUniqueOptions(leads, (contact) => contact.company_name), [leads]);

  const filterDefinitions = useMemo(() => [
    { id: 'owner', columnId: 'assigned_salesperson', label: 'Assigned salesperson', type: 'select', options: ownerOptions, quick: true, getValue: getOwnerId },
    { id: 'created', columnId: 'created_at', label: 'Create date', type: 'date-range', quick: true },
    { id: 'updated', columnId: 'updated_at', label: 'Last activity date', type: 'date-range', quick: true },
    { id: 'status', columnId: 'lead_status', label: 'Status', type: 'select', options: statusOptions, quick: true },
    { id: 'company', columnId: 'company_name', label: 'Company', type: 'select', options: companyOptions },
    { id: 'priority', columnId: 'priority', label: 'Priority', type: 'select', options: priorityFilterOptions },
  ], [companyOptions, ownerOptions, priorityFilterOptions, statusOptions]);

  const list = useCrmListState({
    items: leads,
    columns,
    views,
    filterDefinitions,
    getRowId: (contact) => contact.id,
    initialSort: { columnId: 'created_at', direction: 'desc' },
    initialPageSize: CONTACTS_PER_PAGE,
  });

  const getCurrentViewConfig = () => ({
    search: list.search,
    filters: list.filters,
    sort: list.sort,
    columnPreferences: list.columnPreferences,
    pageSize: list.pageSize,
  });

  const applyViewConfig = (config = {}) => {
    list.setSearch(config.search || '');
    list.setFilters(config.filters && typeof config.filters === 'object' ? config.filters : {});
    list.setSort(config.sort || { columnId: 'created_at', direction: 'desc' });
    if (config.columnPreferences) list.applyColumnPreferences(config.columnPreferences);
    if ([20, 50, 100].includes(Number(config.pageSize))) list.setPageSize(Number(config.pageSize));
  };

  useEffect(() => {
    let active = true;
    preferencesHydratedRef.current = false;
    getListingConfiguration('contacts').then((payload) => {
      if (!active) return;
      const remoteViews = Array.isArray(payload?.views) ? payload.views.map((view) => ({
        id: String(view.id), label: view.name, config: view.config || {},
      })) : [];
      if (remoteViews.length) {
        setSavedViews(remoteViews);
        persistSavedViews(remoteViews);
      }
      const preferences = payload?.preferences || {};
      defaultViewStateRef.current = preferences.allView || preferences.current || null;
      const requestedView = String(preferences.activeView || 'all');
      const validView = requestedView === 'all' || remoteViews.some((view) => view.id === requestedView) ? requestedView : 'all';
      list.setActiveView(validView);
      applyViewConfig(preferences.current || preferences.allView || {});
      preferencesHydratedRef.current = true;
    }).catch(() => {
      preferencesHydratedRef.current = true;
    });
    return () => {
      active = false;
      if (preferencesTimerRef.current) clearTimeout(preferencesTimerRef.current);
    };
    // Listing setters are stable; hydrate only after the contact column schema is ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnConfigReady]);

  useEffect(() => {
    if (!preferencesHydratedRef.current) return undefined;
    if (preferencesTimerRef.current) clearTimeout(preferencesTimerRef.current);
    preferencesTimerRef.current = setTimeout(() => {
      const current = getCurrentViewConfig();
      if (list.activeView === 'all') defaultViewStateRef.current = current;
      saveListingPreferences('contacts', {
        activeView: list.activeView,
        current,
        allView: defaultViewStateRef.current || current,
      }).catch(() => {});
      if (String(list.activeView) !== 'all') {
        const activeSaved = savedViews.find((view) => String(view.id) === String(list.activeView));
        setSavedViews((previous) => {
          const next = previous.map((view) => String(view.id) === String(list.activeView) ? { ...view, config: current } : view);
          persistSavedViews(next);
          return next;
        });
        if (/^\d+$/.test(String(list.activeView))) updateListingView('contacts', Number(list.activeView), activeSaved?.label || 'Saved view', current).catch(() => {});
      }
    }, 450);
    return () => { if (preferencesTimerRef.current) clearTimeout(preferencesTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.activeView, list.search, list.filters, list.sort, list.columnPreferences, list.pageSize]);

  const handleViewChange = (viewId) => {
    if (viewId === list.activeView) return;
    const outgoing = getCurrentViewConfig();
    if (list.activeView === 'all') defaultViewStateRef.current = outgoing;
    else {
      const outgoingView = savedViews.find((view) => String(view.id) === String(list.activeView));
      setSavedViews((previous) => {
        const next = previous.map((view) => String(view.id) === String(list.activeView) ? { ...view, config: outgoing } : view);
        persistSavedViews(next);
        return next;
      });
      if (/^\d+$/.test(String(list.activeView))) updateListingView('contacts', Number(list.activeView), outgoingView?.label || 'Saved view', outgoing).catch(() => {});
    }
    list.setActiveView(viewId);

    if (viewId === 'all') {
      if (defaultViewStateRef.current) applyViewConfig(defaultViewStateRef.current);
      return;
    }

    const savedView = savedViews.find((view) => String(view.id) === String(viewId));
    if (savedView) applyViewConfig(savedView.config);
  };

  const saveCurrentView = async (label) => {
    const normalizedLabel = label.trim();
    if (!normalizedLabel) return;
    const existing = savedViews.find((view) => view.label.toLowerCase() === normalizedLabel.toLowerCase());
    let savedView;
    try {
      const remote = await saveListingView('contacts', normalizedLabel, getCurrentViewConfig());
      savedView = { id: String(remote.id), label: remote.name, config: remote.config || getCurrentViewConfig() };
    } catch {
      savedView = { id: existing?.id || `contact-view-${Date.now()}`, label: normalizedLabel, config: getCurrentViewConfig() };
    }
    const nextViews = existing
      ? savedViews.map((view) => view.id === existing.id ? savedView : view)
      : [...savedViews, savedView];
    setSavedViews(nextViews);
    persistSavedViews(nextViews);
    list.setActiveView(savedView.id);
  };

  const exportSelected = () => {
    const selected = leads.filter((contact) => list.selectedIds.includes(contact.id));
    exportContacts(selected, configuredFields, 'selected_contacts.xlsx');
  };

  const exportAllVisible = () => {
    exportContacts(list.processedItems, configuredFields, 'contacts.xlsx');
  };

  const deleteSelected = () => {
    if (!list.selectedIds.length) return;
    setDeleteDialog({
      open: true,
      mode: 'bulk',
      contactId: null,
      label: `${list.selectedIds.length} contact${list.selectedIds.length === 1 ? '' : 's'}`,
      loading: false,
    });
  };

  const confirmDelete = async () => {
    setDeleteDialog((previous) => ({ ...previous, loading: true }));
    try {
      if (deleteDialog.mode === 'bulk') {
        await onBulkDelete?.(list.selectedIds);
        list.setSelectedIds([]);
      } else {
        await onDeleteContact?.(deleteDialog.contactId);
        list.setSelectedIds((previous) => previous.filter((id) => id !== deleteDialog.contactId));
      }
      setDeleteDialog({ open: false, mode: 'single', contactId: null, label: '', loading: false });
    } catch (deleteError) {
      console.error('Contact deletion failed', deleteError);
      setDeleteDialog((previous) => ({ ...previous, loading: false }));
    }
  };

  const hideColumn = (columnId) => {
    list.applyColumnPreferences({
      ...list.columnPreferences,
      hidden: [...new Set([...list.columnPreferences.hidden, columnId])],
    });
  };

  const togglePinnedColumn = (columnId) => {
    list.applyColumnPreferences({
      ...list.columnPreferences,
      pinned: list.columnPreferences.pinned === columnId ? null : columnId,
    });
  };

  const handleDeleteView = async (viewId) => {
    if (String(viewId) === 'all') return;
    try {
      if (/^\d+$/.test(String(viewId))) await deleteListingView('contacts', Number(viewId));
      setSavedViews((previous) => {
        const next = previous.filter((view) => String(view.id) !== String(viewId));
        persistSavedViews(next);
        return next;
      });
      if (String(list.activeView) === String(viewId)) list.setActiveView('all');
    } catch (error) {
      console.error('Failed to delete contact view', error);
    }
  };

  const previewRecord = preview.record;
  const previewName = getContactName(previewRecord || {});

  return (
    <>
      <CrmListPage
        title="Contacts"
        views={views}
        activeView={list.activeView}
        onViewChange={handleViewChange}
        onAddView={() => setSaveViewOpen(true)}
        onDeleteView={handleDeleteView}
        onCreate={() => navigate('/leads/new')}
        createLabel="Add contact"
        onImport={onImportBulk}
        chromeCollapsed={chromeCollapsed}
        toolbarProps={{
          search: list.search,
          onSearchChange: list.setSearch,
          columns: list.orderedColumns,
          sort: list.sort,
          onSortChange: list.setSort,
          filterDefinitions,
          filters: list.filters,
          onFilterChange: list.setFilter,
          onClearFilter: list.clearFilter,
          activeFilters: list.activeFilterList,
          onClearFilters: list.clearFilters,
          onAdvancedFilters: () => {
            setAdvancedFilterId(null);
            setAdvancedFiltersOpen(true);
          },
          onEditColumns: () => setColumnManagerOpen(true),
          filtersVisible,
          onToggleFilters: () => setFiltersVisible((previous) => !previous),
          chromeCollapsed,
          onToggleChrome: () => setChromeCollapsed((previous) => !previous),
        }}
        selectedCount={list.selectedIds.length}
        onExportSelected={exportSelected}
        onDeleteSelected={deleteSelected}
        footerProps={{
          totalItems: list.processedItems.length,
          itemLabel: 'contact',
          currentPage: list.currentPage,
          totalPages: list.totalPages,
          pageSize: list.pageSize,
          onPageSizeChange: list.setPageSize,
          onPageChange: list.setCurrentPage,
          onRefresh,
          onExport: exportAllVisible,
        }}
      >
        <CrmDataTable
          columns={list.visibleColumns}
          rows={list.pageItems}
          getRowId={(contact) => contact.id}
          selectedIds={list.selectedIds}
          allSelected={list.allProcessedSelected}
          onToggleSelected={list.toggleSelected}
          onToggleSelectAll={list.toggleSelectAll}
          sort={list.sort}
          onSort={list.setSort}
          pinnedColumnId={list.columnPreferences.pinned}
          onTogglePin={togglePinnedColumn}
          onHideColumn={hideColumn}
          onEditColumns={() => setColumnManagerOpen(true)}
          onFilterColumn={(column) => {
            const definition = filterDefinitions.find((filter) => filter.columnId === column.id);
            setAdvancedFilterId(definition?.id || null);
            setAdvancedFiltersOpen(true);
          }}
          onRowClick={handleRowClick}
          onColumnResize={list.setColumnWidth}
          loading={loading}
          error={error}
          onRetry={onRetry}
          onClearFilters={list.activeFilterList.length || list.search ? () => {
            list.setSearch('');
            list.clearFilters();
          } : undefined}
        />
      </CrmListPage>

      <CrmColumnManager
        open={columnManagerOpen}
        onClose={() => setColumnManagerOpen(false)}
        columns={list.orderedColumns}
        preferences={list.columnPreferences}
        onApply={list.applyColumnPreferences}
        onRestoreDefaults={list.restoreDefaultColumns}
      />

      <CrmAdvancedFilters
        open={advancedFiltersOpen}
        onClose={() => setAdvancedFiltersOpen(false)}
        definitions={filterDefinitions}
        filters={list.filters}
        onApply={list.setFilters}
        onClear={list.clearFilters}
        initialFilterId={advancedFilterId}
      />

      <CrmSaveViewModal
        open={saveViewOpen}
        onClose={() => setSaveViewOpen(false)}
        onSave={saveCurrentView}
      />

      <ConfirmDialog
        open={deleteDialog.open}
        title={deleteDialog.mode === 'bulk' ? 'Delete selected contacts?' : 'Delete contact?'}
        message={deleteDialog.mode === 'bulk'
          ? `This will permanently delete ${deleteDialog.label}. This action cannot be undone.`
          : `This will permanently delete ${deleteDialog.label || 'this contact'}. This action cannot be undone.`}
        confirmText="Delete"
        loading={deleteDialog.loading}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialog({ open: false, mode: 'single', contactId: null, label: '', loading: false })}
      />

      <CrmRecordPreview
        open={preview.open}
        onClose={() => {
          previewRequestRef.current += 1;
          setPreview({ open: false, id: null, record: null, loading: false, error: '' });
        }}
        loading={preview.loading}
        error={preview.error}
        onRetry={() => previewRecord && loadPreview(previewRecord)}
        identity={previewRecord ? {
          name: previewName,
          subtitle: previewRecord.company_name || previewRecord.email,
          initials: getInitials(previewName),
        } : null}
        actions={previewRecord ? [
          {
            label: 'Email',
            icon: <EmailOutlinedIcon />,
            disabled: !previewRecord.email,
            onClick: () => { window.location.href = `mailto:${previewRecord.email}`; },
          },
          {
            label: 'Call',
            icon: <PhoneOutlinedIcon />,
            disabled: !previewRecord.phone_number,
            onClick: () => { window.location.href = `tel:${previewRecord.phone_number}`; },
          },
        ] : []}
        sections={previewRecord ? [
          {
            title: 'Key information',
            fields: [
              { label: 'Email', value: previewRecord.email },
              { label: 'Phone', value: previewRecord.phone_number },
              { label: 'Company', value: previewRecord.company_name },
              { label: 'Assigned salesperson', value: getOwnerDisplay(previewRecord) },
              { label: 'Status', value: formatStatusLabel(previewRecord.lead_status) },
              { label: 'Priority', value: formatFieldLabel(previewRecord.priority) },
            ],
          },
          {
            title: 'Recent activity',
            fields: [
              { label: 'Last updated', value: formatLocalDate(previewRecord.updated_at) },
              { label: 'Follow-up date', value: formatLocalDate(previewRecord.follow_up_date) },
              { label: 'Notes', value: previewRecord.notes },
            ],
          },
        ] : []}
        onViewRecord={previewRecord ? () => openFullRecord(previewRecord) : undefined}
      />
    </>
  );
};

export default LeadsTable;
